import { useEffect, useMemo, useState } from 'react';
import { Plus, Wrench } from 'lucide-react';
import { operationsApi, type AssetRegistry, type MaintenanceTicket, type WorkOrder } from '../api';
import { tenantApi } from '@/features/auth/api';
import { PageHeader, SmartLoading } from '@/lib/ui';

type Tab = 'tickets' | 'work-orders';
type Severity = MaintenanceTicket['severity'];
type Member = { user_id: string; full_name: string; email: string; role: string };

const severityClass: Record<Severity, string> = {
  LOW: 'bg-slate-500/15 text-slate-300',
  MEDIUM: 'bg-amber-500/15 text-amber-300',
  HIGH: 'bg-orange-500/15 text-orange-300',
  CRITICAL: 'bg-rose-500/15 text-rose-300',
};

const woStatusClass: Record<WorkOrder['status'], string> = {
  SCHEDULED: 'bg-blue-500/15 text-blue-300',
  IN_PROGRESS: 'bg-amber-500/15 text-amber-300',
  AWAITING_PARTS: 'bg-orange-500/15 text-orange-300',
  COMPLETED: 'bg-emerald-500/15 text-emerald-300',
  CANCELLED: 'bg-zinc-500/15 text-zinc-300',
};

const SEVERITIES: Severity[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

function isImageUrl(p: string): boolean {
  return /^https?:\/\//i.test(p);
}

function money(minor: number, currency = 'AED'): string {
  return `${(minor / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} ${currency}`;
}

export function MaintenancePage() {
  const [tab, setTab] = useState<Tab>('tickets');
  const [tickets, setTickets] = useState<MaintenanceTicket[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrder[]>([]);
  const [assets, setAssets] = useState<AssetRegistry[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showTriage, setShowTriage] = useState(false);
  const [showCreateWo, setShowCreateWo] = useState(false);

  const assetLabel = useMemo(() => {
    const m = new Map(assets.map((a) => [a.id, `${a.code} · ${a.name}`]));
    return (id: string) => m.get(id) ?? id;
  }, [assets]);

  const memberLabel = useMemo(() => {
    const m = new Map(members.map((u) => [u.user_id, u.full_name || u.email]));
    return (id: string | null) => (id ? (m.get(id) ?? id) : '—');
  }, [members]);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [t, w] = await Promise.all([
        operationsApi.listTickets(),
        operationsApi.listWorkOrders(),
      ]);
      setTickets(t);
      setWorkOrders(w);
    } catch (e) {
      setError((e as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    operationsApi
      .listAssets({ limit: 500 })
      .then((r) => setAssets(r.items))
      .catch(() => undefined);
    tenantApi
      .members()
      .then((rows) => setMembers(rows as Member[]))
      .catch(() => undefined);
  }, []);

  const selected = tickets.find((t) => t.id === selectedId) ?? null;
  const linkedWorkOrders = selected
    ? workOrders.filter((w) => w.id === selected.work_order_id || w.asset_id === selected.asset_id)
    : [];

  const onAdvanceWo = async (w: WorkOrder, newStatus: WorkOrder['status']) => {
    try {
      await operationsApi.patchWorkOrder(w.id, { status: newStatus });
      await refresh();
    } catch (e) {
      setError((e as Error).message || 'Failed to update work order');
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border px-6 py-5">
        <PageHeader
          title="Maintenance"
          icon={Wrench}
          subtitle="Field-raised tickets and ops-scheduled work orders."
          actions={
            <button className="btn-primary" onClick={() => setShowNewTicket(true)}>
              <Plus className="h-4 w-4" />
              New Ticket
            </button>
          }
          className="!mb-0"
        />
      </div>

      <div className="flex gap-1 border-b border-border px-4">
        <TabButton active={tab === 'tickets'} onClick={() => setTab('tickets')}>
          Tickets
          {tickets.length > 0 && (
            <span className="ml-2 rounded bg-rose-500/20 px-1 text-xs text-rose-300">
              {tickets.length}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === 'work-orders'} onClick={() => setTab('work-orders')}>
          Work Orders
        </TabButton>
      </div>

      {error && (
        <div className="border-b border-rose-500/40 bg-rose-500/10 px-6 py-2 text-sm text-rose-300">
          {error}
        </div>
      )}

      {tab === 'tickets' ? (
        <div className="flex flex-1 overflow-hidden">
          {/* ── Left: ticket list ─────────────────────────────────── */}
          <div className="flex w-[400px] flex-col border-r border-border">
            <div className="flex-1 overflow-auto">
              {loading ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">Loading…</p>
              ) : tickets.length === 0 ? (
                <p className="px-4 py-3 text-sm text-muted-foreground">No open tickets.</p>
              ) : (
                tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedId(t.id)}
                    className={
                      'block w-full border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-muted/30 ' +
                      (selectedId === t.id ? 'bg-muted/50' : '')
                    }
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate font-medium">{t.title}</div>
                      <span
                        className={
                          'shrink-0 rounded px-2 py-0.5 text-xs ' + severityClass[t.severity]
                        }
                      >
                        {t.severity}
                      </span>
                    </div>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {t.description}
                    </p>
                    <div className="mt-2 flex justify-between text-xs text-muted-foreground">
                      <span>{t.status}</span>
                      <span>{new Date(t.raised_at).toLocaleString()}</span>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* ── Right: detail ─────────────────────────────────────── */}
          <div className="flex-1 overflow-auto p-6">
            {!selected ? (
              <p className="text-sm text-muted-foreground">Select a ticket to view its detail.</p>
            ) : (
              <TicketDetail
                ticket={selected}
                assetLabel={assetLabel}
                memberLabel={memberLabel}
                linkedWorkOrders={linkedWorkOrders}
                onTriage={() => setShowTriage(true)}
                onCreateWo={() => setShowCreateWo(true)}
                onAdvanceWo={onAdvanceWo}
              />
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-auto p-6">
          <WorkOrderTable
            workOrders={workOrders}
            loading={loading}
            assetLabel={assetLabel}
            memberLabel={memberLabel}
            onAdvanceWo={onAdvanceWo}
          />
        </div>
      )}

      {showNewTicket && (
        <NewTicketModal
          assets={assets}
          onClose={() => setShowNewTicket(false)}
          onCreated={async (created) => {
            setShowNewTicket(false);
            await refresh();
            setTab('tickets');
            setSelectedId(created.id);
          }}
        />
      )}

      {showTriage && selected && (
        <TriageModal
          ticket={selected}
          members={members}
          onClose={() => setShowTriage(false)}
          onDone={async () => {
            setShowTriage(false);
            await refresh();
          }}
        />
      )}

      {showCreateWo && selected && (
        <CreateWorkOrderModal
          ticket={selected}
          members={members}
          onClose={() => setShowCreateWo(false)}
          onDone={async () => {
            setShowCreateWo(false);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={
        'border-b-2 px-3 py-2 text-sm ' +
        (active
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground')
      }
    >
      {children}
    </button>
  );
}

// ── Ticket detail (right pane) ─────────────────────────────────────────────
function TicketDetail({
  ticket,
  assetLabel,
  memberLabel,
  linkedWorkOrders,
  onTriage,
  onCreateWo,
  onAdvanceWo,
}: {
  ticket: MaintenanceTicket;
  assetLabel: (id: string) => string;
  memberLabel: (id: string | null) => string;
  linkedWorkOrders: WorkOrder[];
  onTriage: () => void;
  onCreateWo: () => void;
  onAdvanceWo: (w: WorkOrder, s: WorkOrder['status']) => void;
}) {
  const canTriage = ticket.status === 'OPEN' || ticket.status === 'TRIAGED';
  const canCreateWo = ticket.status === 'OPEN' || ticket.status === 'TRIAGED';

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{ticket.title}</h2>
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className={'rounded px-2 py-0.5 text-xs ' + severityClass[ticket.severity]}>
              {ticket.severity}
            </span>
            <span className="text-muted-foreground">{ticket.status}</span>
          </div>
        </div>
        <div className="flex shrink-0 gap-2">
          {canTriage && (
            <button className="btn-secondary" onClick={onTriage}>
              Triage
            </button>
          )}
          {canCreateWo && (
            <button className="btn-primary" onClick={onCreateWo}>
              Create Work Order →
            </button>
          )}
        </div>
      </div>

      <dl className="mt-6 grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
        <Row k="Asset" v={assetLabel(ticket.asset_id)} />
        <Row k="Raised" v={new Date(ticket.raised_at).toLocaleString()} />
        <Row k="Assigned to" v={memberLabel(ticket.assigned_to_user_id)} />
        <Row
          k="Triaged"
          v={ticket.triaged_at ? new Date(ticket.triaged_at).toLocaleString() : '—'}
        />
      </dl>

      <div className="mt-4 whitespace-pre-wrap rounded border border-border bg-muted/30 px-3 py-2 text-sm">
        {ticket.description}
      </div>

      {ticket.rejection_reason && (
        <div className="mt-3 rounded bg-rose-500/10 p-2 text-sm text-rose-300">
          Rejected: {ticket.rejection_reason}
        </div>
      )}

      {/* WEB-TC-66: inline media playback for attached photos */}
      {ticket.photo_paths.length > 0 && (
        <div className="mt-5">
          <div className="text-xs uppercase text-muted-foreground">
            Photos ({ticket.photo_paths.length})
          </div>
          <div className="mt-2 flex flex-wrap gap-3">
            {ticket.photo_paths.map((p, i) =>
              isImageUrl(p) ? (
                <a key={i} href={p} target="_blank" rel="noreferrer">
                  <img
                    src={p}
                    alt={`attachment ${i + 1}`}
                    className="h-32 w-32 rounded border border-border object-cover"
                  />
                </a>
              ) : (
                <span
                  key={i}
                  className="rounded border border-border bg-muted/30 px-2 py-1 font-mono text-xs text-muted-foreground"
                >
                  {p}
                </span>
              ),
            )}
          </div>
        </div>
      )}

      {/* Linked / asset work orders + state controls (WEB-TC-65) */}
      <h3 className="mt-8 text-lg font-semibold">Work Orders</h3>
      <table className="mt-2 w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-4">Title</th>
            <th className="py-2 pr-4">Status</th>
            <th className="py-2 pr-4">Parts</th>
            <th className="py-2 pr-4">Labor</th>
            <th className="py-2 pr-4">Workshop</th>
            <th className="py-2"></th>
          </tr>
        </thead>
        <tbody>
          {linkedWorkOrders.map((w) => (
            <tr key={w.id} className="border-b border-border/60">
              <td className="py-2 pr-4">
                {w.title}
                {w.id === ticket.work_order_id && (
                  <span className="ml-2 rounded bg-primary/20 px-1 text-xs text-primary">
                    linked
                  </span>
                )}
              </td>
              <td className="py-2 pr-4">
                <span className={'rounded px-2 py-0.5 text-xs ' + woStatusClass[w.status]}>
                  {w.status}
                </span>
              </td>
              <td className="py-2 pr-4 tabular-nums">{money(w.parts_cost_minor, w.currency)}</td>
              <td className="py-2 pr-4 tabular-nums">{money(w.labor_cost_minor, w.currency)}</td>
              <td className="py-2 pr-4">{w.vendor_name ?? '—'}</td>
              <td className="py-2 text-right">
                <WoStateActions wo={w} onAdvance={onAdvanceWo} />
              </td>
            </tr>
          ))}
          {linkedWorkOrders.length === 0 && (
            <tr>
              <td colSpan={6} className="py-4 text-center text-sm text-muted-foreground">
                No work orders for this asset yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between border-b border-border/40 py-1.5">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className="text-right">{v}</dd>
    </div>
  );
}

// ── Work order state transition buttons (WEB-TC-65) ────────────────────────
function WoStateActions({
  wo,
  onAdvance,
}: {
  wo: WorkOrder;
  onAdvance: (w: WorkOrder, s: WorkOrder['status']) => void;
}) {
  return (
    <span className="space-x-3 text-xs">
      {wo.status === 'SCHEDULED' && (
        <button
          className="text-primary hover:underline"
          onClick={() => onAdvance(wo, 'IN_PROGRESS')}
        >
          Start
        </button>
      )}
      {wo.status === 'IN_PROGRESS' && (
        <>
          <button
            className="text-amber-300 hover:underline"
            onClick={() => onAdvance(wo, 'AWAITING_PARTS')}
          >
            Await parts
          </button>
          <button
            className="text-emerald-300 hover:underline"
            onClick={() => onAdvance(wo, 'COMPLETED')}
          >
            Complete
          </button>
        </>
      )}
      {wo.status === 'AWAITING_PARTS' && (
        <button
          className="text-primary hover:underline"
          onClick={() => onAdvance(wo, 'IN_PROGRESS')}
        >
          Resume
        </button>
      )}
      {(wo.status === 'SCHEDULED' ||
        wo.status === 'IN_PROGRESS' ||
        wo.status === 'AWAITING_PARTS') && (
        <button
          className="text-rose-300 hover:underline"
          onClick={() => onAdvance(wo, 'CANCELLED')}
        >
          Cancel
        </button>
      )}
      {(wo.status === 'COMPLETED' || wo.status === 'CANCELLED') && (
        <span className="text-muted-foreground">—</span>
      )}
    </span>
  );
}

// ── Full work-order table tab ──────────────────────────────────────────────
function WorkOrderTable({
  workOrders,
  loading,
  assetLabel,
  memberLabel,
  onAdvanceWo,
}: {
  workOrders: WorkOrder[];
  loading: boolean;
  assetLabel: (id: string) => string;
  memberLabel: (id: string | null) => string;
  onAdvanceWo: (w: WorkOrder, s: WorkOrder['status']) => void;
}) {
  if (loading) return <SmartLoading className="text-sm text-muted-foreground" />;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-muted-foreground">
        <tr className="border-b border-border">
          <th className="py-2 pr-4">Title</th>
          <th className="py-2 pr-4">Asset</th>
          <th className="py-2 pr-4">Status</th>
          <th className="py-2 pr-4">Assignee</th>
          <th className="py-2 pr-4">Parts</th>
          <th className="py-2 pr-4">Labor</th>
          <th className="py-2 pr-4">Workshop</th>
          <th className="py-2"></th>
        </tr>
      </thead>
      <tbody>
        {workOrders.map((w) => (
          <tr key={w.id} className="border-b border-border/60">
            <td className="py-2 pr-4">{w.title}</td>
            <td className="py-2 pr-4 text-xs text-muted-foreground">{assetLabel(w.asset_id)}</td>
            <td className="py-2 pr-4">
              <span className={'rounded px-2 py-0.5 text-xs ' + woStatusClass[w.status]}>
                {w.status}
              </span>
            </td>
            <td className="py-2 pr-4 text-xs">{memberLabel(w.assigned_to_user_id)}</td>
            <td className="py-2 pr-4 tabular-nums">{money(w.parts_cost_minor, w.currency)}</td>
            <td className="py-2 pr-4 tabular-nums">{money(w.labor_cost_minor, w.currency)}</td>
            <td className="py-2 pr-4">{w.vendor_name ?? '—'}</td>
            <td className="py-2 text-right">
              <WoStateActions wo={w} onAdvance={onAdvanceWo} />
            </td>
          </tr>
        ))}
        {workOrders.length === 0 && (
          <tr>
            <td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">
              No active work orders.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

// ── WEB-TC-62: New Ticket modal ────────────────────────────────────────────
function NewTicketModal({
  assets,
  onClose,
  onCreated,
}: {
  assets: AssetRegistry[];
  onClose: () => void;
  onCreated: (created: MaintenanceTicket) => void;
}) {
  const [assetId, setAssetId] = useState('');
  const [severity, setSeverity] = useState<Severity>('MEDIUM');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const addPhoto = () => {
    const url = photoUrl.trim();
    if (!url) return;
    if (photos.length >= 5) {
      setError('A ticket can carry at most 5 photos');
      return;
    }
    setPhotos((p) => [...p, url]);
    setPhotoUrl('');
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetId) {
      setError('Select an asset');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const created = await operationsApi.createTicket({
        asset_id: assetId,
        severity,
        title: title.trim(),
        description: description.trim(),
        raised_at: new Date().toISOString(),
        photo_paths: photos,
      });
      onCreated(created);
    } catch (e) {
      setError((e as Error).message || 'Failed to create ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="New Maintenance Ticket"
      onClose={onClose}
      onSubmit={onSubmit}
      error={error}
      saving={saving}
      submitLabel="Create ticket"
    >
      <div className="grid grid-cols-2 gap-3">
        <Field label="Asset *" wide>
          <select
            required
            className="input"
            value={assetId}
            onChange={(e) => setAssetId(e.target.value)}
          >
            <option value="">Select an asset…</option>
            {assets.map((a) => (
              <option key={a.id} value={a.id}>
                {a.code} · {a.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Severity *">
          <select
            className="input"
            value={severity}
            onChange={(e) => setSeverity(e.target.value as Severity)}
          >
            {SEVERITIES.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </Field>
        <Field label="Title *">
          <input
            required
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Description *" wide>
          <textarea
            required
            rows={3}
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field label="Attach photo (image URL)" wide>
          <div className="flex gap-2">
            <input
              className="input flex-1"
              placeholder="https://…"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addPhoto();
                }
              }}
            />
            <button type="button" className="btn-secondary" onClick={addPhoto}>
              Add
            </button>
          </div>
        </Field>
      </div>
      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <div key={i} className="relative">
              {isImageUrl(p) ? (
                <img
                  src={p}
                  alt={`photo ${i + 1}`}
                  className="h-20 w-20 rounded border border-border object-cover"
                />
              ) : (
                <span className="block max-w-[8rem] truncate rounded border border-border bg-muted/30 px-2 py-1 font-mono text-xs">
                  {p}
                </span>
              )}
              <button
                type="button"
                onClick={() => setPhotos((arr) => arr.filter((_, j) => j !== i))}
                className="absolute -right-2 -top-2 rounded-full bg-rose-500 px-1.5 text-xs text-white"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </ModalShell>
  );
}

// ── WEB-TC-63: Triage modal (severity + assignee + decision) ───────────────
function TriageModal({
  ticket,
  members,
  onClose,
  onDone,
}: {
  ticket: MaintenanceTicket;
  members: Member[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [decision, setDecision] = useState<'TRIAGE' | 'REJECT'>('TRIAGE');
  const [severity, setSeverity] = useState<Severity>(ticket.severity);
  const [assignee, setAssignee] = useState(ticket.assigned_to_user_id ?? '');
  const [rejectionReason, setRejectionReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (decision === 'REJECT' && !rejectionReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await operationsApi.triageTicket(ticket.id, {
        decision,
        severity,
        assigned_to_user_id: assignee || undefined,
        rejection_reason: decision === 'REJECT' ? rejectionReason.trim() : undefined,
      });
      onDone();
    } catch (e) {
      setError((e as Error).message || 'Failed to triage ticket');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title={`Triage: ${ticket.title}`}
      onClose={onClose}
      onSubmit={onSubmit}
      error={error}
      saving={saving}
      submitLabel={decision === 'REJECT' ? 'Reject ticket' : 'Mark triaged'}
      maxW="max-w-md"
    >
      <Field label="Decision" wide>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setDecision('TRIAGE')}
            className={
              'flex-1 rounded border px-3 py-2 text-sm ' +
              (decision === 'TRIAGE'
                ? 'border-primary bg-primary/10 text-foreground'
                : 'border-border text-muted-foreground')
            }
          >
            Triage (mark for WO)
          </button>
          <button
            type="button"
            onClick={() => setDecision('REJECT')}
            className={
              'flex-1 rounded border px-3 py-2 text-sm ' +
              (decision === 'REJECT'
                ? 'border-rose-500 bg-rose-500/10 text-foreground'
                : 'border-border text-muted-foreground')
            }
          >
            Reject
          </button>
        </div>
      </Field>
      <Field label="Severity" wide>
        <select
          className="input"
          value={severity}
          onChange={(e) => setSeverity(e.target.value as Severity)}
        >
          {SEVERITIES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
      </Field>
      <Field label="Assign to" wide>
        <select className="input" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">Unassigned</option>
          {members.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.full_name || m.email} · {m.role}
            </option>
          ))}
        </select>
      </Field>
      {decision === 'REJECT' && (
        <Field label="Rejection reason *" wide>
          <textarea
            rows={2}
            className="input"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </Field>
      )}
    </ModalShell>
  );
}

// ── WEB-TC-64: Create Work Order modal ─────────────────────────────────────
function CreateWorkOrderModal({
  ticket,
  members,
  onClose,
  onDone,
}: {
  ticket: MaintenanceTicket;
  members: Member[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(ticket.title);
  const [description, setDescription] = useState(ticket.description);
  const [vendorName, setVendorName] = useState('');
  const [partsCost, setPartsCost] = useState('');
  const [laborCost, setLaborCost] = useState('');
  const [assignee, setAssignee] = useState(ticket.assigned_to_user_id ?? '');
  const [dueAt, setDueAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await operationsApi.createWorkOrder({
        asset_id: ticket.asset_id,
        from_ticket_id: ticket.id,
        title: title.trim(),
        description: description.trim() || undefined,
        vendor_name: vendorName.trim() || undefined,
        parts_cost_minor: partsCost ? Math.round(Number(partsCost) * 100) : 0,
        labor_cost_minor: laborCost ? Math.round(Number(laborCost) * 100) : 0,
        assigned_to_user_id: assignee || undefined,
        due_at: dueAt ? new Date(dueAt).toISOString() : undefined,
        currency: 'AED',
      });
      onDone();
    } catch (e) {
      setError((e as Error).message || 'Failed to create work order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      title="Create Work Order"
      onClose={onClose}
      onSubmit={onSubmit}
      error={error}
      saving={saving}
      submitLabel="Create work order"
    >
      <p className="rounded bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        Linked to ticket “{ticket.title}”. New work order starts in SCHEDULED.
      </p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Title *" wide>
          <input
            required
            className="input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Description" wide>
          <textarea
            rows={2}
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
        <Field label="Workshop / vendor" wide>
          <input
            className="input"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            placeholder="e.g. Al Futtaim Service Center"
          />
        </Field>
        <Field label="Parts cost (AED)">
          <input
            type="number"
            step="0.01"
            min="0"
            className="input"
            value={partsCost}
            onChange={(e) => setPartsCost(e.target.value)}
          />
        </Field>
        <Field label="Labor cost (AED)">
          <input
            type="number"
            step="0.01"
            min="0"
            className="input"
            value={laborCost}
            onChange={(e) => setLaborCost(e.target.value)}
          />
        </Field>
        <Field label="Assign to">
          <select className="input" value={assignee} onChange={(e) => setAssignee(e.target.value)}>
            <option value="">Unassigned</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.full_name || m.email}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Due date">
          <input
            type="date"
            className="input"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
          />
        </Field>
      </div>
    </ModalShell>
  );
}

// ── Shared modal shell + Field ─────────────────────────────────────────────
function ModalShell({
  title,
  onClose,
  onSubmit,
  error,
  saving,
  submitLabel,
  maxW = 'max-w-lg',
  children,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  error: string | null;
  saving: boolean;
  submitLabel: string;
  maxW?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-4">
      <form onSubmit={onSubmit} className={'card w-full space-y-3 bg-surface p-6 ' + maxW}>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button type="button" onClick={onClose} className="text-muted-foreground">
            ✕
          </button>
        </div>
        {error && (
          <div className="rounded bg-rose-500/15 px-3 py-2 text-sm text-rose-300">{error}</div>
        )}
        {children}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary">
            {saving ? 'Saving…' : submitLabel}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <label className={'flex flex-col gap-1 text-sm ' + (wide ? 'col-span-2' : '')}>
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
